import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { capitalize } from 'lodash';

const LIMIT = 4;

export default class PageHeadersFiltersLayerComponent extends Component {
  @service router;
  @service store;
  @service betaToggle;
  @tracked showMore = false;

  personIdentifier = {
    fields: ['pms', 'product_managers', 'authors', 'assignees'],
    model: 'person',
    icon: 'user',
  };
  teamIdentifier = { fields: ['teams'], model: 'team', icon: 'teams' };
  themeIdentifier = { fields: ['themes'], model: 'theme', icon: 'theme' };
  statusIdentifier = { fields: ['status'] };
  sourceIdentifier = { fields: ['sources'] };
  roadmapIdentifier = { fields: ['roadmaps'], model: 'roadmap' };

  hiddenFilters = ['start_date', 'end_date', 'columns', 'roadmap'];
  // groupByIdentifier = { fields: ['grouping'] };

  get activeFilters() {
    const currentRoute = this.router.currentRoute;
    return this.extractActiveFiltersFromURL(currentRoute);
  }

  get extraFiltersLength() {
    return this.activeFilters.slice(LIMIT).length;
  }

  get showMoreButton() {
    return !this.showMore && this.activeFilters.length > LIMIT;
  }

  get showLessButton() {
    return this.showMore;
  }

  get filters() {
    if (this.showMore) return this.activeFilters;
    else return this.activeFilters.slice(0, LIMIT);
  }

  extractActiveFiltersFromURL(currentRoute) {
    const arr = [];
    const queryParams = currentRoute?.queryParams;
    if (!queryParams) return arr;
    for (let [key, value] of Object.entries(queryParams)) {
      let isJson = this.isJsonArray(value);
      if (this.hiddenFilters.includes(key)) continue;
      else if (isJson) {
        const parsedValues = JSON.parse(value);
        const filterObjects = this.formatParsedValues(key, parsedValues);
        arr.splice(arr.length - 1, 0, ...filterObjects);
      } else if (key === 'grouping')
        arr.push({ label: capitalize(value), value, key, icon: 'grouping' });
      else arr.push({ label: capitalize(value), value, key });
    }
    return arr;
  }

  /**
   *
   * @param {string} key
   * @param {Array} parsedValues
   */
  formatParsedValues(key, parsedValues) {
    let arr = [];
    if (this.personIdentifier.fields.includes(key)) {
      parsedValues.forEach((item) => {
        const person = this.store.peekRecord(this.personIdentifier.model, item);
        arr.push({
          label: person?.name,
          type: 'person',
          value: person?.id,
          icon: this.personIdentifier.icon,
          color: person?.color,
          abbreviation: person?.abbreviation,
          key,
        });
      });
      return arr;
    }

    if (this.teamIdentifier.fields.includes(key)) {
      parsedValues.forEach((item) => {
        const team = this.store.peekRecord(this.teamIdentifier.model, item);
        arr.push({
          label: team?.name,
          type: 'team',
          value: team?.id,
          icon: this.teamIdentifier.icon,
          key,
        });
      });
      return arr;
    }

    if (this.themeIdentifier.fields.includes(key)) {
      parsedValues.forEach((item) => {
        const theme = this.store.peekRecord(this.themeIdentifier.model, item);
        arr.push({
          label: theme?.title,
          type: 'theme',
          value: theme?.id,
          color: theme?.color,
          icon: this.themeIdentifier.icon,
          key,
        });
      });
      return arr;
    }

    if (this.statusIdentifier.fields.includes(key)) {
      parsedValues.forEach((item) => {
        const statusObject = this.betaToggle.statusOptions.find(
          (option) => option.value === item,
        );
        arr.push({
          label: statusObject?.label,
          type: 'status',
          value: statusObject?.value,
          key,
        });
      });
      return arr;
    }

    if (this.sourceIdentifier.fields.includes(key)) {
      parsedValues.forEach((item) => {
        const sourceObject = this.betaToggle.sourceOptions.find(
          (option) => option.value === item,
        );
        arr.push({
          label: sourceObject?.label,
          type: 'source',
          value: sourceObject?.value,
          key,
        });
      });
      return arr;
    }

    if (this.roadmapIdentifier.fields.includes(key)) {
      parsedValues.forEach((item) => {
        const roadmap = this.store.peekRecord(
          this.roadmapIdentifier.model,
          item,
        );
        arr.push({
          label: roadmap?.title,
          type: 'roadmap',
          value: roadmap?.id,
          key,
        });
      });
      return arr;
    }

    parsedValues.forEach((item) => {
      arr.push({
        label: item,
        value: item,
        key,
      });
    });

    return arr;
  }

  isJsonArray(str) {
    let json;
    try {
      json = JSON.parse(str);
    } catch {
      return false;
    }
    return Array.isArray(json);
  }

  @action
  toggleShowMore() {
    this.showMore = !this.showMore;
  }

  @action
  handleFilterDelete(filter) {
    this.args.handleFilterDelete(filter);
  }
}
